import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import seoSettingApi from '../../api/seoSettingApi';
import { ROUTES } from '../../constants';
import { useToast } from '../../contexts/ToastContext';

const SEOSettings = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [seoSettings, setSeoSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSeoSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await seoSettingApi.getSeoSettings();
      if (response.success && response.data) {
        setSeoSettings(response.data);
      } else {
        setSeoSettings([]);
        addToast(response.message || 'No SEO settings found.', 'info');
      }
    } catch (err) {
      console.error('Failed to fetch SEO settings:', err);
      setError('Failed to load SEO settings. Please try again later.');
      addToast(err.response?.data?.message || 'Failed to load SEO settings.', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchSeoSettings();
  }, [fetchSeoSettings]);

  const handleEditSetting = (id) => {
    navigate(ROUTES.SEO_SETTINGS_EDIT.replace(':id', id));
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-text-white">SEO Settings Management</h1>
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={5} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Page</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Meta Title</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {seoSettings.length > 0 ? (
                  seoSettings.map((setting) => (
                    <tr key={setting.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{setting.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{setting.page}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{setting.meta_title}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm">
                        <Button
                          onClick={() => handleEditSetting(setting.id)}
                          className="text-yellow-500 hover:bg-yellow-600"
                          icon={faEdit}
                          tooltipId={`edit-seo-setting-tooltip-${setting.id}`}
                          tooltipContent="Edit"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-3 px-4 text-center text-sm text-text-gray">No SEO settings found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default SEOSettings;